import type { Options } from '@/models/option';
import { DEFAULT_TAX_RATE } from '@/wabunka-src/constants/enExperiences';

import type { WabunkaExperienceInterpreterOption } from '@japan-culture-and-technology/wabunka-protos/ts_proto/wabunka_experience_interpreter_option';
import type { WabunkaInterpreterOption } from '@japan-culture-and-technology/wabunka-protos/ts_proto/wabunka_interpreter_option';

/**
 * Wabunka 通訳オプションビューモデル
 */
export interface WabunkaInterpreterOptionViewModel {
  /** 通訳オプションマスタID */
  id: string;
  /** 通訳オプション名 */
  name: string;
  /** 説明 */
  description: string;
  /** 金額（税抜） */
  price: number;
  /** シーケンスID */
  sequenceId: number;
  /**
   * 追加プロパティ
   */
  priceIncludingTax: number;
}

/**
 * 通訳オプションビューモデル のデフォルト値を返却する
 *
 * @returns {WabunkaInterpreterOptionViewModel} - 通訳オプションビューモデルのデフォルト値
 */
export const createDefaultWabunkaInterpreterOptionViewModel = (): WabunkaInterpreterOptionViewModel => ({
  id: '',
  name: '',
  description: '',
  price: 0,
  sequenceId: 1,
  priceIncludingTax: 0,
});

/**
 * セレクトボックス用配列の組み立て
 * @param {WabunkaInterpreterOptionViewModel} interpreterOptions - 通訳オプションビューモデル
 * @returns {Options} - 選択肢の配列
 */
export const buildWabunkaInterpreterOptionChoices = (interpreterOptions: WabunkaInterpreterOptionViewModel[]): Options => {
  if (!interpreterOptions) return [];

  return interpreterOptions
    .sort((a, b) => {
      // シーケンスIDの昇順でソート
      return a.sequenceId - b.sequenceId;
    })
    .map((_) => ({ label: `${_.name}（Paid : +JPY ${_.priceIncludingTax}）`, value: _.id }));
};

export const buildWabunkaInterpreterOptionListViewModel = (res: WabunkaExperienceInterpreterOption[]): WabunkaInterpreterOptionViewModel[] => {
  return res
    .filter((interpreterOption) => {
      return interpreterOption !== undefined;
    })
    .map((experiencesInterpreterOption) => {
      const interpreterOption = experiencesInterpreterOption.interpreterOption as WabunkaInterpreterOption;
      return {
        id: interpreterOption.id,
        name: interpreterOption.name,
        description: interpreterOption.description,
        price: interpreterOption.price,
        sequenceId: experiencesInterpreterOption.sequenceId,
        priceIncludingTax: calcPriceIncludingTax(interpreterOption.price, DEFAULT_TAX_RATE),
      };
    });
};

/**
 * 通訳手配要否に基づいて言語対応テキストを返却する
 * @param isInterpreterNeeded - 通訳手配が必要かどうか
 * @param interpreterOptions - 通訳オプション一覧
 * @returns string
 */
export const interpreterOptionText = (isInterpreterNeeded: boolean, interpreterOptions: WabunkaInterpreterOptionViewModel[]): string => {
  if (!isInterpreterNeeded) {
    return 'English-speaking host';
  }
  const languages = interpreterOptions.map((option) => option.name);
  if (languages.length === 0) {
    return 'English-speaking guide available';
  }
  if (languages.length === 1) {
    return `${languages[0]}-speaking guide included`;
  }
  return `${languages.join('/')} guide available`;
};
